import _ from 'lodash'

const QUESTION_VIEW_FIELDS = [
  '_id',
  '_vType',
  '_parent',
  'searchedChildren',
  'isShow',
  '_treeDepth',
  '_treeParentKey',
  '_treeParentLabel',
  '_treeRootKey',
  '_treeChildLinks',
  '_isTreeNode',
  '_treeDisabled',
  '_treeDisabledReason'
]

const OPTION_VIEW_FIELDS = [
  'child_question',
  '_treeChildTitle'
]

export const OPEN_QUESTION_TREE_MODE = {
  FLAT: 'flat',
  TREE: 'tree'
}

export const MAX_TREE_DEPTH = 5

function defaultIsLinkedOption (option) {
  const source = option && (option.sourceOption || option)
  return Boolean(source && (
    source.child_question || source.child_question_id || source.child_question_vid || source.next_question_id
  ))
}

export function reconcileOpenQuestionOptions ({
  previousOptions = [],
  optionValues = [],
  createOptionKey,
  isLinkedOption = defaultIsLinkedOption
}) {
  const sameCount = optionValues.length === previousOptions.length
  const usedOptionKeys = new Set()
  const previousCounts = {}
  const nextCounts = {}

  previousOptions.forEach(option => {
    const key = option.value || ''
    previousCounts[key] = (previousCounts[key] || 0) + 1
  })
  optionValues.forEach(value => {
    const key = value || ''
    nextCounts[key] = (nextCounts[key] || 0) + 1
  })

  const ambiguousLinkedChange = Object.keys(previousCounts).some(key => {
    if (previousCounts[key] === nextCounts[key] || previousCounts[key] < 2) return false
    return previousOptions.some(option => (option.value || '') === key && isLinkedOption(option))
  })

  const options = optionValues.map((value, index) => {
    const matchingOptions = previousOptions.filter(option => {
      return !usedOptionKeys.has(option._vid) && option.value === value
    })
    let matchedOption = matchingOptions.find(option => previousOptions[index] === option)

    if (!matchedOption && matchingOptions.length === 1) {
      matchedOption = matchingOptions[0]
    }
    if (!matchedOption && matchingOptions.length > 1) {
      matchedOption = matchingOptions[0]
    }
    if (!matchedOption && sameCount) {
      const positionOption = previousOptions[index]
      if (positionOption && !usedOptionKeys.has(positionOption._vid)) {
        matchedOption = positionOption
      }
    }

    const optionKey = matchedOption ? matchedOption._vid : createOptionKey(index)
    usedOptionKeys.add(optionKey)
    return {
      _vid: optionKey,
      index,
      label: value,
      value,
      sourceOption: matchedOption ? matchedOption.sourceOption : null
    }
  })

  return { options, ambiguousLinkedChange }
}

function toNumber (value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

function normalizeBoolean (value) {
  return value === true || value === 1 || value === '1'
}

function normalizeTreeMode (value) {
  return value === OPEN_QUESTION_TREE_MODE.TREE || normalizeBoolean(value)
}

function normalizeArray (value) {
  if (_.isArray(value)) {
    return value
  }
  if (value === null || value === undefined || value === '') {
    return []
  }
  return [value]
}

export function isAnsweredValue (answer) {
  if (_.isArray(answer)) {
    return answer.length > 0
  }
  if (_.isPlainObject(answer)) {
    return Boolean(answer.videoKey || answer.description)
  }
  return !(answer === null || answer === undefined || answer === '')
}

export function buildQuestionTreeMeta (question = {}) {
  const rawOptions = question.options || question.option || []
  return {
    depth: Math.max(toNumber(question.depth) || 0, 0),
    isTerminal: normalizeBoolean(question.is_terminal),
    treePath: question.tree_path || null,
    parentQuestionId: toNumber(question.parent_question_id),
    parentOptionId: toNumber(question.parent_option_id),
    optionTreeMap: _.map(rawOptions, option => {
      const nextQuestionId = toNumber(
        option.next_question_id ||
        option.nextQuestionId ||
        option.next_question?.id
      )
      return {
        id: toNumber(option.id),
        label: option.description || option.label || '',
        nextQuestionId,
        hasNextQuestion: normalizeBoolean(option.has_next_question) || Boolean(nextQuestionId),
        isKnockout: normalizeBoolean(option.is_knockout)
      }
    })
  }
}

export function enhanceTreeQuestion (question = {}) {
  const treeMeta = buildQuestionTreeMeta(question)
  return Object.assign(question, {
    treeMeta,
    treeLabel: `L${treeMeta.depth}`
  })
}

export function detectTreeMode ({ questions = [], meta = {} } = {}) {
  if (normalizeTreeMode(meta.mode) || normalizeTreeMode(meta.has_tree) || normalizeTreeMode(meta.hasTree)) {
    return OPEN_QUESTION_TREE_MODE.TREE
  }
  const hasTreeField = _.some(questions, question => {
    const treeMeta = question.treeMeta || buildQuestionTreeMeta(question)
    return (
      treeMeta.depth > 0 ||
      treeMeta.isTerminal ||
      Boolean(treeMeta.treePath) ||
      Boolean(treeMeta.parentQuestionId) ||
      _.some(treeMeta.optionTreeMap, option => option.hasNextQuestion)
    )
  })
  return hasTreeField ? OPEN_QUESTION_TREE_MODE.TREE : OPEN_QUESTION_TREE_MODE.FLAT
}

function buildQuestionMap (questionList = []) {
  return _.keyBy(questionList, 'id')
}

function buildTreeRelations (questionList = []) {
  const questionMap = buildQuestionMap(questionList)
  const childQuestionIds = new Set()
  const questionChildrenMap = {}
  _.forEach(questionList, question => {
    const optionTreeMap = _.get(question, 'treeMeta.optionTreeMap', buildQuestionTreeMeta(question).optionTreeMap)
    _.forEach(optionTreeMap, option => {
      if (!option.nextQuestionId) {
        return
      }
      childQuestionIds.add(option.nextQuestionId)
      if (!questionChildrenMap[question.id]) {
        questionChildrenMap[question.id] = []
      }
      questionChildrenMap[question.id].push(option.nextQuestionId)
    })
  })
  const rootQuestionList = _.sortBy(
    _.filter(questionList, question => {
      return !childQuestionIds.has(question.id)
    }),
    ['order', 'id']
  )
  return {
    questionMap,
    questionChildrenMap,
    rootQuestionList
  }
}

export function getSelectedOptionIds (question = {}) {
  return _.chain(normalizeArray(question.answer))
    .map(item => (_.isPlainObject(item) ? item.id : item))
    .map(toNumber)
    .filter(item => item !== null)
    .value()
}

export function getTriggeredNextQuestionIds (question = {}) {
  const selectedOptionIds = getSelectedOptionIds(question)
  if (_.isEmpty(selectedOptionIds)) {
    return []
  }
  const selectedOptions = _.filter(question.treeMeta?.optionTreeMap || [], option => selectedOptionIds.includes(option.id))
  if (_.some(selectedOptions, 'isKnockout')) {
    return []
  }
  return _.chain(selectedOptions)
    .filter(option => option.nextQuestionId)
    .map('nextQuestionId')
    .uniq()
    .value()
}

export function getVisibleTreeQuestionIds (questionList = []) {
  const { questionMap, rootQuestionList } = buildTreeRelations(questionList)
  const visibleQuestionIds = []
  const pushQuestion = question => {
    if (!question || visibleQuestionIds.includes(question.id)) {
      return
    }
    visibleQuestionIds.push(question.id)
    _.forEach(getTriggeredNextQuestionIds(question), nextQuestionId => {
      pushQuestion(questionMap[nextQuestionId])
    })
  }
  _.forEach(rootQuestionList, pushQuestion)
  return visibleQuestionIds
}

export function getVisibleTreeQuestions (questionList = []) {
  const questionMap = buildQuestionMap(questionList)
  return _.chain(getVisibleTreeQuestionIds(questionList))
    .map(questionId => questionMap[questionId])
    .filter(Boolean)
    .value()
}

function getAnswerLabel (question) {
  if (_.isArray(question.answer)) {
    if (!_.isEmpty(question.optionList)) {
      return _.chain(question.optionList)
        .filter(option => question.answer.includes(option.value))
        .map('label')
        .join(', ')
        .value()
    }
    return question.answer.join(', ')
  }
  if (_.isPlainObject(question.answer)) {
    return question.uploaderFileDescription || question.answer.description || ''
  }
  if (!_.isEmpty(question.optionList)) {
    const matchedOption = _.find(question.optionList, { value: question.answer })
    if (matchedOption) {
      return matchedOption.label
    }
  }
  return question.answer
}

export function getTreeAnswerPath (questionList = []) {
  return _.chain(getVisibleTreeQuestions(questionList))
    .filter(question => isAnsweredValue(question.answer))
    .map(question => {
      const selectedOptionIds = getSelectedOptionIds(question)
      const selectedOptions = _.chain(question.treeMeta?.optionTreeMap || [])
        .filter(option => selectedOptionIds.includes(option.id))
        .map(option => ({ id: option.id, label: option.label }))
        .value()
      const selectedOption = _.head(selectedOptions)
      return {
        id: question.id,
        title: question.title,
        depth: _.get(question, 'treeMeta.depth', 0),
        depthLabel: question.treeLabel || `L${_.get(question, 'treeMeta.depth', 0)}`,
        answer: question.answer,
        answerLabel: getAnswerLabel(question),
        selectedOptions,
        optionId: selectedOption?.id || null,
        optionLabel: selectedOption?.label || null
      }
    })
    .value()
}

export function getTreeKnockoutState (questionList = []) {
  return _.some(getVisibleTreeQuestions(questionList), question => {
    const selectedOptionIds = getSelectedOptionIds(question)
    return _.some(question.treeMeta?.optionTreeMap, option => {
      return selectedOptionIds.includes(option.id) && option.isKnockout
    })
  })
}

export function getTreeProgress (questionList = []) {
  const visibleQuestionList = getVisibleTreeQuestions(questionList)
  const answeredCount = _.filter(visibleQuestionList, question => isAnsweredValue(question.answer)).length
  const total = visibleQuestionList.length
  return {
    current: answeredCount,
    total,
    percent: total > 0 ? Math.round((answeredCount / total) * 100) : 0
  }
}

export function resetQuestionAnswer (question) {
  if (!question) {
    return question
  }
  if (_.isArray(question.answer)) {
    question.answer = []
  } else if (_.isPlainObject(question.answer)) {
    question.answer = {
      ...question.answer,
      videoKey: null,
      description: null
    }
  } else {
    question.answer = null
  }
  if (question.answer_value !== undefined) {
    question.answer_value = ''
  }
  if (question.answer_file_key !== undefined) {
    question.answer_file_key = null
  }
  if (question.uploaderFileDescription !== undefined) {
    question.uploaderFileDescription = null
  }
  question.errorMessage = null
  question.isUploading = false
  return question
}

export function clearChildTreeAnswers (questionList = [], questionId, clearAnswer = resetQuestionAnswer) {
  const { questionMap, questionChildrenMap } = buildTreeRelations(questionList)
  const queue = [...(questionChildrenMap[questionId] || [])]
  const clearedQuestionIds = []
  while (!_.isEmpty(queue)) {
    const childQuestionId = queue.shift()
    if (clearedQuestionIds.includes(childQuestionId)) {
      continue
    }
    if (!questionMap[childQuestionId]) {
      continue
    }
    clearedQuestionIds.push(childQuestionId)
    clearAnswer(questionMap[childQuestionId])
    queue.push(...(questionChildrenMap[childQuestionId] || []))
  }
  return clearedQuestionIds
}

export function serializeQuestionTreeState (questionList = []) {
  const visibleQuestionList = getVisibleTreeQuestions(questionList)
  const answerPath = getTreeAnswerPath(questionList)
  const currentQuestion = _.find(visibleQuestionList, question => !isAnsweredValue(question.answer)) || _.last(visibleQuestionList) || null
  return {
    currentQuestion,
    visibleQuestionIds: _.map(visibleQuestionList, 'id'),
    answerPath,
    answeredQuestions: _.keyBy(answerPath, 'id'),
    progress: getTreeProgress(questionList),
    isKnockout: getTreeKnockoutState(questionList)
  }
}

export function buildTreeSubmitPayload (questionList = [], { includeCurrentPath = true } = {}) {
  const answer = {}
  _.forEach(getVisibleTreeQuestions(questionList), question => {
    if (!isAnsweredValue(question.answer)) {
      return
    }
    answer[question.id] = question.answer
  })
  const payload = { answer }
  if (includeCurrentPath) {
    payload.current_path = _.map(getTreeAnswerPath(questionList), node => {
      const pathItem = { question_id: node.id }
      const optionIds = getSelectedOptionIds({ answer: node.answer })
      if (optionIds.length === 1) {
        pathItem.option_id = optionIds[0]
      } else if (optionIds.length > 1) {
        pathItem.option_ids = optionIds
      }
      return pathItem
    })
  }
  return payload
}

export function getQuestionKey (question) {
  return (question && (question.id || question._vid)) || null
}

export function getOptionChildKey (option) {
  if (!option) {
    return null
  }
  if (option.child_question) {
    return getQuestionKey(option.child_question)
  }
  return option.child_question_id || option.child_question_vid || option.next_question_id || null
}

export function hasOptionChild (option) {
  return Boolean(getOptionChildKey(option))
}

export function getComparableQuestionKey (questionOrKey) {
  const key = _.isObject(questionOrKey) ? getQuestionKey(questionOrKey) : questionOrKey
  return key === undefined || key === null ? null : String(key)
}

export function getComparableOptionKey (optionOrKey) {
  const key = _.isObject(optionOrKey) ? (optionOrKey.id || optionOrKey._vid) : optionOrKey
  return key === undefined || key === null ? null : String(key)
}

function getCanonicalOptionChildKey (option) {
  if (!option) {
    return null
  }
  return option.child_question_id ||
    option.child_question_vid ||
    option.next_question_id ||
    getQuestionKey(option.child_question) ||
    null
}

export function getQuestionSubtreeKeys (questionList = [], questionOrKey) {
  const questionMap = {}
  _.forEach(questionList, question => {
    const questionKey = getComparableQuestionKey(question)
    if (questionKey) {
      questionMap[questionKey] = question
    }
  })

  const rootKey = getComparableQuestionKey(questionOrKey)
  if (!rootKey || !questionMap[rootKey]) {
    return []
  }

  const subtreeKeys = new Set()
  const collect = questionKey => {
    if (!questionKey || subtreeKeys.has(questionKey) || !questionMap[questionKey]) {
      return
    }
    subtreeKeys.add(questionKey)
    _.forEach(questionMap[questionKey].options || [], option => {
      collect(getComparableQuestionKey(getCanonicalOptionChildKey(option)))
    })
  }
  collect(rootKey)
  return Array.from(subtreeKeys)
}

export function setQuestionSubtreeVisibility (questionList = [], questionOrKey, visibility) {
  const subtreeKeys = new Set(getQuestionSubtreeKeys(questionList, questionOrKey))
  _.forEach(questionList, question => {
    if (subtreeKeys.has(getComparableQuestionKey(question))) {
      question.visibility = visibility
    }
  })
  return questionList
}

// 候选列表和保存路径共用同一条拟议边校验，避免过期弹窗绕过循环检查。
export function canAttachQuestionAsChild (questionList = [], parentOrKey, childOrKey) {
  const parentKey = getComparableQuestionKey(parentOrKey)
  const childKey = getComparableQuestionKey(childOrKey)
  if (!parentKey || !childKey || parentKey === childKey) {
    return false
  }

  const questionMap = {}
  let hasInvalidGraph = false
  _.forEach(questionList, question => {
    const questionKey = getComparableQuestionKey(question)
    if (!questionKey) {
      hasInvalidGraph = true
      return
    }
    if (questionMap[questionKey]) {
      hasInvalidGraph = true
      return
    }
    questionMap[questionKey] = question
  })
  _.forEach(questionList, question => {
    _.forEach((question && question.options) || [], option => {
      const nextKey = getComparableQuestionKey(getCanonicalOptionChildKey(option))
      if ((option.child_question && !nextKey) || (nextKey && !questionMap[nextKey])) {
        hasInvalidGraph = true
      }
    })
  })
  if (hasInvalidGraph || !questionMap[parentKey] || !questionMap[childKey]) {
    return false
  }

  const visited = new Set()
  const visiting = new Set()
  const isUnsafeSubtree = questionKey => {
    if (questionKey === parentKey) {
      return true
    }
    if (visiting.has(questionKey)) {
      return true
    }
    if (visited.has(questionKey)) {
      return false
    }
    visiting.add(questionKey)
    const question = questionMap[questionKey]
    const isUnsafe = Boolean(question && _.some(question.options || [], option => {
      const nextKey = getComparableQuestionKey(getCanonicalOptionChildKey(option))
      return nextKey && isUnsafeSubtree(nextKey)
    }))
    visiting.delete(questionKey)
    visited.add(questionKey)
    return isUnsafe
  }

  return !isUnsafeSubtree(childKey)
}

export function removeQuestionSubtree (questionList = [], questionOrKey) {
  const questionMap = {}
  _.forEach(questionList, question => {
    const questionKey = getComparableQuestionKey(question)
    if (questionKey) {
      questionMap[questionKey] = question
    }
  })

  const removedKeys = new Set(getQuestionSubtreeKeys(questionList, questionOrKey))
  if (!removedKeys.size) {
    return {
      questions: _.cloneDeep(questionList),
      removedKeys: []
    }
  }

  const questions = _.chain(questionList)
    .filter(question => !removedKeys.has(getComparableQuestionKey(question)))
    .map(question => {
      const remainingQuestion = _.cloneDeep(question)
      _.forEach(remainingQuestion.options || [], option => {
        const childKey = getComparableQuestionKey(getCanonicalOptionChildKey(option))
        const childQuestion = childKey && questionMap[childKey]

        // child_question 是展示缓存，删除后必须统一丢弃，避免 normalize 时从旧嵌套对象
        // 重新收集已经删除的后代。仅有展示缓存的未保存关系先补为规范 ID/VID 引用。
        if (childQuestion && !removedKeys.has(childKey) &&
          !option.child_question_id && !option.child_question_vid && !option.next_question_id) {
          if (childQuestion.id) {
            option.child_question_id = childQuestion.id
            option.next_question_id = childQuestion.id
          } else if (childQuestion._vid) {
            option.child_question_vid = childQuestion._vid
          }
        }
        option.child_question = null

        if (removedKeys.has(childKey)) {
          option.child_question_id = null
          option.child_question_vid = null
          option.next_question_id = null
        }
      })
      return remainingQuestion
    })
    .value()

  return {
    questions,
    removedKeys: Array.from(removedKeys)
  }
}

function cloneQuestionFields (question) {
  return _.cloneDeep(_.omit(question, [...QUESTION_VIEW_FIELDS, 'options']))
}

function cloneOptionFields (option) {
  return _.cloneDeep(_.omit(option, OPTION_VIEW_FIELDS))
}

export function normalizeEditableQuestionTree (questionList = [], generateTempVid) {
  const questionMap = {}
  const questionKeys = []
  const sourceKeyMap = new Map()

  const ensureQuestionKey = question => {
    const existingKey = getQuestionKey(question)
    if (existingKey) {
      return existingKey
    }
    if (!sourceKeyMap.has(question)) {
      sourceKeyMap.set(question, generateTempVid())
    }
    return sourceKeyMap.get(question)
  }

  const collectQuestion = question => {
    if (!question) {
      return null
    }

    const sourceQuestionKey = ensureQuestionKey(question)
    const questionKey = getComparableQuestionKey(sourceQuestionKey)
    if (questionMap[questionKey]) {
      return questionKey
    }

    const normalizedQuestion = cloneQuestionFields(question)
    if (!normalizedQuestion.id) {
      normalizedQuestion._vid = sourceQuestionKey
    }
    normalizedQuestion.parent_question_id = null
    normalizedQuestion.depth = 0
    questionMap[questionKey] = normalizedQuestion
    questionKeys.push(questionKey)

    normalizedQuestion.options = _.map(question.options || [], (option, index) => {
      const normalizedOption = cloneOptionFields(option)
      if (!normalizedOption.id && !normalizedOption._vid) {
        normalizedOption._vid = `${sourceQuestionKey}_opt_${index}`
      }

      if (option.child_question) {
        const childKey = collectQuestion(option.child_question)
        const childQuestion = questionMap[childKey]
        if (childQuestion && childQuestion.id) {
          normalizedOption.child_question_id = childQuestion.id
          normalizedOption.next_question_id = childQuestion.id
        } else if (childQuestion) {
          normalizedOption.child_question_vid = childQuestion._vid
          normalizedOption.child_question_id = null
          normalizedOption.next_question_id = null
        }
      } else if (normalizedOption.next_question_id && !normalizedOption.child_question_id) {
        normalizedOption.child_question_id = normalizedOption.next_question_id
      }
      return normalizedOption
    })

    return questionKey
  }

  _.forEach(questionList, collectQuestion)

  const normalizedQuestions = _.map(questionKeys, key => questionMap[key])
  const childIds = new Set()
  _.forEach(normalizedQuestions, question => {
    _.forEach(question.options || [], option => {
      const childKey = getComparableQuestionKey(getOptionChildKey(option))
      const childQuestion = questionMap[childKey]
      option.child_question = childQuestion || null
      if (childQuestion) {
        childIds.add(childKey)
      }
    })
  })

  const visited = new Set()
  const assignDepth = (question, depth, parentKey, ancestry) => {
    const questionKey = getComparableQuestionKey(question)
    if (!questionKey || ancestry.has(questionKey)) {
      return
    }
    if (!visited.has(questionKey)) {
      question.depth = depth
      question.parent_question_id = parentKey
      visited.add(questionKey)
    }

    const nextAncestry = new Set(ancestry)
    nextAncestry.add(questionKey)
    _.forEach(question.options || [], option => {
      if (option.child_question) {
        assignDepth(option.child_question, depth + 1, getQuestionKey(question), nextAncestry)
      }
    })
  }

  const rootQuestions = _.filter(normalizedQuestions, question => !childIds.has(getComparableQuestionKey(question)))
  _.forEach(rootQuestions, question => assignDepth(question, 0, null, new Set()))
  _.forEach(normalizedQuestions, question => {
    if (!visited.has(getComparableQuestionKey(question))) {
      assignDepth(question, 0, null, new Set())
    }
  })

  return normalizedQuestions
}

export function getRootQuestions (questionList = []) {
  const childKeys = new Set()
  _.forEach(questionList, question => {
    _.forEach(question.options || [], option => {
      const childKey = getOptionChildKey(option)
      if (childKey) {
        childKeys.add(childKey)
      }
    })
  })
  const getStablePosition = question => {
    return typeof question._serverStablePosition === 'number' &&
      Number.isFinite(question._serverStablePosition)
      ? question._serverStablePosition
      : Infinity
  }
  return _.orderBy(
    _.filter(questionList, question => !childKeys.has(getQuestionKey(question))),
    ['order', getStablePosition, 'id'],
    ['asc', 'asc', 'asc']
  )
}

export function decorateQuestionTreeRows (questionList = []) {
  const questionMap = _.keyBy(questionList, getQuestionKey)
  const parentMap = {}

  _.forEach(questionList, question => {
    question._treeDepth = 0
    question._treeParentKey = null
    question._treeParentLabel = ''
    question._treeRootKey = getQuestionKey(question)
    question._treeChildLinks = []
    question._isTreeNode = false
    _.forEach(question.options || [], option => {
      option._treeChildTitle = ''
    })
  })

  _.forEach(questionList, question => {
    _.forEach(question.options || [], option => {
      const childQuestion = questionMap[getOptionChildKey(option)]
      if (!childQuestion) {
        return
      }
      const childKey = getQuestionKey(childQuestion)
      const optionLabel = option.description || '-'
      question._isTreeNode = true
      childQuestion._isTreeNode = true
      option._treeChildTitle = childQuestion.title
      question._treeChildLinks.push(`${optionLabel} -> ${childQuestion.title}`)
      if (!parentMap[childKey]) {
        parentMap[childKey] = {
          parent: question,
          optionLabel
        }
      }
    })
  })

  const rows = []
  const visited = new Set()
  const appendQuestion = (question, depth, rootKey, ancestry) => {
    const questionKey = getQuestionKey(question)
    if (!questionKey || visited.has(questionKey) || ancestry.has(questionKey)) {
      return
    }
    visited.add(questionKey)
    question._treeDepth = depth
    question._treeRootKey = rootKey
    const parentInfo = parentMap[questionKey]
    question._treeParentKey = parentInfo ? getQuestionKey(parentInfo.parent) : null
    question._treeParentLabel = parentInfo ? `${parentInfo.parent.title} / ${parentInfo.optionLabel}` : ''
    rows.push(question)

    const nextAncestry = new Set(ancestry)
    nextAncestry.add(questionKey)
    _.forEach(_.orderBy(question.options || [], ['order'], ['asc']), option => {
      const childQuestion = questionMap[getOptionChildKey(option)]
      if (childQuestion) {
        appendQuestion(childQuestion, depth + 1, rootKey, nextAncestry)
      }
    })
  }

  _.forEach(getRootQuestions(questionList), question => {
    appendQuestion(question, 0, getQuestionKey(question), new Set())
  })
  _.forEach(questionList, question => {
    if (!visited.has(getQuestionKey(question))) {
      appendQuestion(question, 0, getQuestionKey(question), new Set())
    }
  })
  return rows
}

export function inspectQuestionTree (questionList = [], maxDepth = MAX_TREE_DEPTH) {
  const questionMap = _.keyBy(questionList, getQuestionKey)
  const parentKeys = {}
  const danglingReferences = []
  const cycleKeys = new Set()
  const overDepthKeys = new Set()

  _.forEach(questionList, question => {
    const parentKey = getQuestionKey(question)
    _.forEach(question.options || [], option => {
      const childKey = getOptionChildKey(option)
      if (!childKey) {
        return
      }
      if (!questionMap[childKey]) {
        danglingReferences.push({ parentKey, childKey })
        return
      }
      if (!parentKeys[childKey]) {
        parentKeys[childKey] = []
      }
      parentKeys[childKey].push(parentKey)
    })
  })

  const visit = (question, ancestry, depth) => {
    const questionKey = getQuestionKey(question)
    if (depth > maxDepth) {
      overDepthKeys.add(questionKey)
    }
    if (ancestry.has(questionKey)) {
      cycleKeys.add(questionKey)
      return
    }
    const nextAncestry = new Set(ancestry)
    nextAncestry.add(questionKey)
    _.forEach(question.options || [], option => {
      const childQuestion = questionMap[getOptionChildKey(option)]
      if (childQuestion) {
        visit(childQuestion, nextAncestry, depth + 1)
      }
    })
  }
  _.forEach(getRootQuestions(questionList), question => visit(question, new Set(), 0))
  _.forEach(questionList, question => visit(question, new Set(), 0))

  return {
    danglingReferences,
    cycleKeys: Array.from(cycleKeys),
    overDepthKeys: Array.from(overDepthKeys),
    multipleParentKeys: _.chain(parentKeys)
      .pickBy(keys => keys.length > 1)
      .keys()
      .value()
  }
}

export function cloneSelectedQuestionSubtrees ({
  selectedRoots = [],
  sourceQuestions = [],
  generateTempVid,
  startOrder = 1,
  hasVisibilityOption = false,
  defaultVisibility = null
} = {}) {
  const normalizedSource = normalizeEditableQuestionTree(sourceQuestions, generateTempVid)
  const sourceMap = _.keyBy(normalizedSource, getQuestionKey)
  const selectedKeys = new Set()
  const danglingReferences = []
  const cycleKeys = new Set()

  const collect = (question, ancestry) => {
    const questionKey = getQuestionKey(question)
    if (!questionKey) {
      return
    }
    if (ancestry.has(questionKey)) {
      cycleKeys.add(questionKey)
      return
    }
    if (selectedKeys.has(questionKey)) {
      return
    }
    selectedKeys.add(questionKey)
    const nextAncestry = new Set(ancestry)
    nextAncestry.add(questionKey)
    _.forEach(question.options || [], option => {
      const childKey = getOptionChildKey(option)
      if (!childKey) {
        return
      }
      const childQuestion = sourceMap[childKey]
      if (!childQuestion) {
        danglingReferences.push({ parentKey: questionKey, childKey })
        return
      }
      collect(childQuestion, nextAncestry)
    })
  }

  _.forEach(selectedRoots, root => collect(sourceMap[getQuestionKey(root)], new Set()))
  const selectedSource = _.filter(normalizedSource, question => selectedKeys.has(getQuestionKey(question)))
  const inspection = inspectQuestionTree(selectedSource)
  if (danglingReferences.length || cycleKeys.size || inspection.multipleParentKeys.length || inspection.overDepthKeys.length) {
    return {
      questions: [],
      errors: {
        danglingReferences: [...danglingReferences, ...inspection.danglingReferences],
        cycleKeys: _.uniq([...Array.from(cycleKeys), ...inspection.cycleKeys]),
        multipleParentKeys: inspection.multipleParentKeys,
        overDepthKeys: inspection.overDepthKeys
      }
    }
  }

  const questionVidMap = {}
  const optionVidMap = {}
  _.forEach(selectedSource, question => {
    const questionKey = getQuestionKey(question)
    questionVidMap[questionKey] = generateTempVid()
    _.forEach(question.options || [], (option, index) => {
      optionVidMap[option.id || option._vid || `${questionKey}_${index}`] = generateTempVid()
    })
  })

  const questions = _.map(selectedSource, (question, questionIndex) => {
    const questionKey = getQuestionKey(question)
    const clonedQuestion = cloneQuestionFields(question)
    clonedQuestion.id = null
    clonedQuestion._vid = questionVidMap[questionKey]
    clonedQuestion.parent_question_id = null
    clonedQuestion.order = startOrder + questionIndex
    delete clonedQuestion.group_id
    delete clonedQuestion.status
    delete clonedQuestion.need_update
    if (hasVisibilityOption) {
      clonedQuestion.visibility = defaultVisibility
    }
    clonedQuestion.options = _.map(question.options || [], (option, optionIndex) => {
      const childKey = getOptionChildKey(option)
      const optionKey = option.id || option._vid || `${questionKey}_${optionIndex}`
      const clonedOption = cloneOptionFields(option)
      clonedOption.id = null
      clonedOption._vid = optionVidMap[optionKey]
      clonedOption.child_question_id = null
      clonedOption.next_question_id = null
      clonedOption.child_question_vid = childKey ? questionVidMap[childKey] : null
      clonedOption.child_question = null
      return clonedOption
    })
    return clonedQuestion
  })

  return {
    questions: normalizeEditableQuestionTree(questions, generateTempVid),
    errors: null
  }
}

export function reorderRootSubtrees (questionList = [], rootOrder = [], generateTempVid) {
  const normalizedQuestions = normalizeEditableQuestionTree(questionList, generateTempVid)
  const questionMap = _.keyBy(normalizedQuestions, getQuestionKey)
  const reorderedQuestions = []
  const visited = new Set()

  const appendQuestion = question => {
    const questionKey = getQuestionKey(question)
    if (!questionKey || visited.has(questionKey)) {
      return
    }
    visited.add(questionKey)
    reorderedQuestions.push(question)
    _.forEach(_.orderBy(question.options || [], ['order'], ['asc']), option => {
      const childQuestion = questionMap[getOptionChildKey(option)]
      if (childQuestion) {
        appendQuestion(childQuestion)
      }
    })
  }

  _.forEach(rootOrder, root => appendQuestion(questionMap[getQuestionKey(root)]))
  _.forEach(getRootQuestions(normalizedQuestions), appendQuestion)
  _.forEach(normalizedQuestions, appendQuestion)
  _.forEach(reorderedQuestions, (question, index) => {
    question.order = index + 1
  })
  return normalizeEditableQuestionTree(reorderedQuestions, generateTempVid)
}

// Profile 筛选结果专用；不参与答题、配置或其他结果页的树计算。
const SELECT_TYPES = ['dropdown', 'checkbox', 'single_select', 'multiple_select']

function idKey (value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null
  const number = Number(value)
  return Number.isSafeInteger(number) && number > 0 ? String(number) : null
}

function optionIds (value) {
  const values = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [value]
  return [...new Set(values.map(idKey).filter(Boolean))]
}

function snapshotOptionIds (node) {
  const singular = optionIds(node.option_id)
  const plural = optionIds(node.option_ids)
  // 两个兼容字段同时存在时必须表达同一组选项，否则快照已自相矛盾。
  if (singular.length && plural.length &&
    (singular.length !== plural.length || singular.some(key => !plural.includes(key)))) {
    return { ids: [], invalid: true }
  }
  return { ids: plural.length ? plural : singular, invalid: false }
}

function hasValue (value) {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.some(hasValue)
  if (value instanceof Date) return !Number.isNaN(value.getTime())
  if (typeof value === 'object') return Object.values(value).some(hasValue)
  return typeof value !== 'number' || Number.isFinite(value)
}

export function hasScreeningAnswer (question) {
  if (SELECT_TYPES.includes(question.type)) {
    return optionIds(question.answer_value).length > 0 ||
      (question.answer_option || []).some(option => Boolean(idKey(option.id)))
  }
  return hasValue(question.answer_value) || hasValue(question.answer_file_key)
}

function parsePath (value) {
  if (value === null || value === undefined || value === '') return { nodes: [], invalid: false }
  try {
    const nodes = typeof value === 'string' ? JSON.parse(value) : value
    return Array.isArray(nodes) && nodes.every(node => node && typeof node === 'object' && !Array.isArray(node))
      ? { nodes, invalid: false }
      : { nodes: [], invalid: true }
  } catch (error) {
    return { nodes: [], invalid: true }
  }
}

function isDeleted (item) {
  return item.is_deleted === true || item.is_deleted === 1 || item.is_deleted === '1'
}

function numericDepth (value) {
  if (value === null || value === undefined || value === '') return null
  const depth = Number(value)
  return Number.isInteger(depth) && depth >= 0 ? depth : null
}

export function buildScreeningQuestionDisplay (questions) {
  const questionMap = new Map()
  const optionMap = new Map()
  const duplicateQuestionIds = new Set()
  questions.forEach(question => {
    const key = idKey(question.id)
    if (!key) return
    if (questionMap.has(key)) duplicateQuestionIds.add(key)
    else questionMap.set(key, question)
  })
  questionMap.forEach((question, parentId) => {
    ;(question.options || []).forEach(option => {
      const key = idKey(option.id)
      if (!key) return
      // 不同题目声明同一选项 ID 时禁止用最后一项覆盖。
      if (optionMap.has(key)) optionMap.set(key, null)
      else optionMap.set(key, { parentId, option })
    })
  })
  const activeParents = new Map()
  optionMap.forEach(entry => {
    if (!entry || isDeleted(entry.option) || isDeleted(questionMap.get(entry.parentId)) ||
      entry.option.is_knockout === true || entry.option.is_knockout === 1 || entry.option.is_knockout === '1' ||
      entry.option.has_next_question === false) return
    const childId = idKey(entry.option.next_question_id)
    if (!childId) return
    if (!activeParents.has(childId)) activeParents.set(childId, [])
    activeParents.get(childId).push(entry)
  })
  const broken = { parent: null, option: null, incomplete: true, child: true }

  function resolvePair (question, parentId, optionId) {
    const parent = questionMap.get(parentId)
    if (!parent || parentId === idKey(question.id)) return { ...broken }
    if (!optionId) return { parent, option: null, incomplete: true, child: true }
    const entry = optionMap.get(optionId)
    if (!entry || entry.parentId !== parentId) return { ...broken }
    return { parent, option: entry.option, incomplete: false, child: true }
  }

  function configuredOptions (question, parentId) {
    return (activeParents.get(idKey(question.id)) || []).filter(entry => !parentId || entry.parentId === parentId)
  }

  function pathSnapshotPairAt (nodes, index) {
    if (index <= 0 || index >= nodes.length) return null
    const node = nodes[index]
    const hasSnapshotRelation = [node.parent_question_id, node.parent_option_id]
      .some(value => value !== null && value !== undefined && value !== '')
    if (!hasSnapshotRelation) return null
    const parentNode = nodes[index - 1]
    const parentId = idKey(node.parent_question_id)
    const optionId = idKey(node.parent_option_id)
    const selectedSnapshot = snapshotOptionIds(parentNode)
    if (selectedSnapshot.invalid) return { invalid: true }
    const selected = selectedSnapshot.ids
    const selectedBelongsToParent = selected.length > 0 && selected.every(key => {
      const entry = optionMap.get(key)
      return entry && entry.parentId === parentId
    })
    if (!parentId || !optionId || parentId !== idKey(parentNode.question_id) ||
      !selectedBelongsToParent || !selected.includes(optionId)) return { invalid: true }
    const entry = optionMap.get(optionId)
    if (!entry || entry.parentId !== parentId) return { invalid: true }
    return { parentId, optionId }
  }

  function snapshotPair (question, nodes) {
    const indexes = nodes.map((node, index) => idKey(node.question_id) === idKey(question.id) ? index : -1).filter(index => index >= 0)
    if (indexes.length > 1) return { ...broken }
    if (indexes.length === 0 && nodes.length) return { ...broken }
    if (!indexes.length || indexes[0] === 0) return null
    const snapshot = pathSnapshotPairAt(nodes, indexes[0])
    if (snapshot) {
      return snapshot.invalid
        ? { ...broken }
        : resolvePair(question, snapshot.parentId, snapshot.optionId)
    }
    const parentNode = nodes[indexes[0] - 1]
    const parentId = idKey(parentNode.question_id)
    const selected = optionIds(parentNode.option_ids || parentNode.option_id)
    const matching = selected.filter(key => {
      const entry = optionMap.get(key)
      return entry && entry.parentId === parentId && idKey(entry.option.next_question_id) === idKey(question.id)
    })
    if (selected.length === 1) return resolvePair(question, parentId, selected[0])
    if (matching.length === 1) return resolvePair(question, parentId, matching[0])
    return { ...broken }
  }

  function relationFor (question, path) {
    const parentValues = [question.parent_question_id, question.answer_parent_question_id].filter(value => value !== null && value !== undefined && value !== '')
    const optionValues = [question.parent_option_id, question.answer_parent_option_id].filter(value => value !== null && value !== undefined && value !== '')
    const parents = [...new Set(parentValues.map(idKey))]
    const options = [...new Set(optionValues.map(idKey))]
    if (parents.includes(null) || options.includes(null) || parents.length > 1 || options.length > 1) return { ...broken }
    if ((parents.length || options.length) && path.nodes.length) {
      const indexes = path.nodes.map((node, index) => idKey(node.question_id) === idKey(question.id) ? index : -1).filter(index => index >= 0)
      if (indexes.length !== 1 || indexes[0] === 0) return { ...broken }
    }
    if (options.length) {
      const entry = optionMap.get(options[0])
      const explicit = resolvePair(question, parents[0] || (entry && entry.parentId), options[0])
      const snapshot = snapshotPair(question, path.nodes)
      if (snapshot && !snapshot.incomplete &&
        (!explicit.parent || idKey(snapshot.parent.id) !== idKey(explicit.parent.id) ||
          idKey(snapshot.option.id) !== idKey(explicit.option && explicit.option.id))) return { ...broken }
      return explicit
    }
    if (parents.length) {
      const snapshot = snapshotPair(question, path.nodes)
      if (snapshot) {
        if (!snapshot.parent || idKey(snapshot.parent.id) !== parents[0]) return { ...broken }
        return snapshot
      }
      const matching = configuredOptions(question, parents[0])
      return matching.length === 1
        ? resolvePair(question, parents[0], idKey(matching[0].option.id))
        : resolvePair(question, parents[0], null)
    }
    if (path.invalid) return { ...broken }
    const snapshot = snapshotPair(question, path.nodes)
    if (snapshot) return snapshot
    const treePath = question.tree_path
    if (treePath) {
      const tokens = String(treePath).match(/^\/question\/\d+(?:\/option\/\d+\/question\/\d+)*$/)
      if (!tokens) return { ...broken }
      const ids = String(treePath).match(/\d+/g)
      if (idKey(ids[ids.length - 1]) !== idKey(question.id)) return { ...broken }
      if (ids.length > 1) return resolvePair(question, idKey(ids[ids.length - 3]), idKey(ids[ids.length - 2]))
    }
    const configured = configuredOptions(question)
    if (configured.length > 1) return { ...broken }
    if (configured.length === 1) return resolvePair(question, configured[0].parentId, idKey(configured[0].option.id))
    return { parent: null, option: null, incomplete: false, child: false }
  }

  function displayPath (path, question) {
    if (path.invalid || !path.nodes.length) return []
    const ids = path.nodes.map(node => idKey(node.question_id))
    if (new Set(ids).size !== ids.length || ids.some(key => !questionMap.has(key)) ||
      ids[ids.length - 1] !== idKey(question.id)) return []
    const result = []
    for (let index = 0; index < path.nodes.length; index++) {
      let optionLabel = ''
      if (index < path.nodes.length - 1) {
        const snapshot = pathSnapshotPairAt(path.nodes, index + 1)
        // 只有逐边关系与祖先已选选项相互印证时，答题路径才可信。
        if (!snapshot || snapshot.invalid || snapshot.parentId !== ids[index]) return []
        const entry = optionMap.get(snapshot.optionId)
        optionLabel = entry.option.description
      }
      result.push({
        questionId: ids[index],
        depth: index,
        title: questionMap.get(ids[index]).title,
        optionLabel
      })
    }
    return result
  }

  const rows = [...questionMap.values()].map(question => {
    const path = parsePath(question.answer_path || question.answer_answer_path)
    const relation = relationFor(question, path)
    let depth = numericDepth(question.depth)
    if (depth === null) depth = path.nodes.length > 1 ? path.nodes.length - 1 : relation.parent ? (numericDepth(relation.parent.depth) || 0) + 1 : 0
    const hasParentEvidence = relation.parent ||
      [question.parent_question_id, question.answer_parent_question_id, question.parent_option_id, question.answer_parent_option_id].some(value => value !== null && value !== undefined && value !== '') ||
      String(question.tree_path || '').includes('/option/') ||
      path.nodes.findIndex(node => idKey(node.question_id) === idKey(question.id)) > 0 || configuredOptions(question).length > 0
    const isRoot = depth === 0 && !hasParentEvidence
    if (!isRoot && !relation.parent) relation.incomplete = true
    return {
      ...question,
      _tree: {
        depth,
        isTreeQuestion: false,
        isTreeRoot: isRoot,
        parent: relation.parent ? { id: relation.parent.id, title: relation.parent.title, depth: numericDepth(relation.parent.depth) || 0 } : null,
        parentQuestionId: relation.parent ? relation.parent.id : null,
        parentAnswer: null,
        triggerOptionLabel: relation.option ? relation.option.description : null,
        relationIncomplete: relation.incomplete,
        hasAnswerPathSnapshot: path.invalid || path.nodes.length > 0,
        answerPath: relation.incomplete || isRoot ? [] : displayPath(path, question)
      }
    }
  })
  const rowMap = new Map(rows.map(row => [idKey(row.id), row]))
  const parentIds = new Map(rows.map(row => [idKey(row.id), idKey(row._tree.parentQuestionId)]))
  rows.forEach(row => {
    const visited = new Set([idKey(row.id)])
    let parentId = idKey(row._tree.parentQuestionId)
    while (parentId && rowMap.has(parentId)) {
      if (visited.has(parentId)) {
        Object.assign(row._tree, { parent: null, parentQuestionId: null, triggerOptionLabel: null, relationIncomplete: true, answerPath: [] })
        break
      }
      visited.add(parentId)
      parentId = parentIds.get(parentId)
    }
  })
  const isTree = rows.some(row => !row._tree.isTreeRoot || row.tree_path) || activeParents.size > 0
  const visibleRows = rows.filter(row => (row._tree.isTreeRoot && !isDeleted(row)) || hasScreeningAnswer(row))
  const visibleIds = new Set(visibleRows.map(row => idKey(row.id)))

  function configuredRootIdFor (row, blockedIds = new Set(), useOwnRelation = true) {
    const rowId = idKey(row.id)
    const visited = new Set([rowId, ...blockedIds])
    let currentId = rowId
    while (currentId) {
      const current = rowMap.get(currentId)
      const ownParentId = useOwnRelation && currentId === rowId && !current._tree.relationIncomplete
        ? idKey(current._tree.parentQuestionId)
        : null
      const candidates = ownParentId ? [] : (activeParents.get(currentId) || [])
      const uniqueParents = ownParentId ? [ownParentId] : [...new Set(candidates.map(entry => entry.parentId))]
      if (!ownParentId && (candidates.length !== 1 || uniqueParents.length !== 1)) return null
      const parentId = ownParentId || uniqueParents[0]
      if (!rowMap.has(parentId) || duplicateQuestionIds.has(parentId) || visited.has(parentId)) return null
      visited.add(parentId)
      const parent = rowMap.get(parentId)
      if (parent._tree.isTreeRoot) return visibleIds.has(parentId) ? parentId : null
      currentId = parentId
    }
    return null
  }

  function historicalRootIdFor (row) {
    const path = parsePath(row.answer_path || row.answer_answer_path)
    const ids = path.nodes.map(node => idKey(node.question_id))
    if (path.invalid || !ids.length || new Set(ids).size !== ids.length ||
      ids.some(key => !questionMap.has(key) || duplicateQuestionIds.has(key)) || ids[ids.length - 1] !== idKey(row.id)) return null

    for (let index = 0; index < path.nodes.length; index++) {
      const node = path.nodes[index]
      const ownOptions = SELECT_TYPES.includes(questionMap.get(ids[index]).type)
        ? snapshotOptionIds(node)
        : { ids: [], invalid: false }
      if (ownOptions.invalid || ownOptions.ids.some(optionId => {
        const entry = optionMap.get(optionId)
        return (optionMap.has(optionId) && !entry) || (entry && entry.parentId !== ids[index])
      })) return null

      if (index === 0) continue
      const parentId = ids[index - 1]
      const suppliedParentIds = [node.parent_question_id, node.answer_parent_question_id]
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(idKey)
      const suppliedParentOptions = [node.parent_option_id, node.answer_parent_option_id]
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(idKey)
      if (suppliedParentIds.includes(null) || suppliedParentOptions.includes(null) ||
        new Set(suppliedParentIds).size > 1 || new Set(suppliedParentOptions).size > 1 ||
        (suppliedParentIds.length && suppliedParentIds[0] !== parentId)) return null
      if (suppliedParentOptions.length) {
        const entry = optionMap.get(suppliedParentOptions[0])
        if (!entry || entry.parentId !== parentId) return null
        const parentOptions = snapshotOptionIds(path.nodes[index - 1])
        if (parentOptions.invalid || (parentOptions.ids.length && !parentOptions.ids.includes(suppliedParentOptions[0]))) return null
      }
    }

    const explicitParentIds = [row.parent_question_id, row.answer_parent_question_id]
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(idKey)
    const explicitParentOptions = [row.parent_option_id, row.answer_parent_option_id]
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(idKey)
    if (explicitParentIds.includes(null) || explicitParentOptions.includes(null) ||
      new Set(explicitParentIds).size > 1 || new Set(explicitParentOptions).size > 1) return null
    const firstNode = path.nodes[0]
    const firstParentIds = [firstNode.parent_question_id, firstNode.answer_parent_question_id]
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(idKey)
    const firstParentOptions = [firstNode.parent_option_id, firstNode.answer_parent_option_id]
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(idKey)
    if (firstParentIds.includes(null) || firstParentOptions.includes(null) ||
      new Set(firstParentIds).size > 1 || new Set(firstParentOptions).size > 1) return null
    const rootId = ids[0]
    const root = rowMap.get(rootId)
    const prefixCandidates = activeParents.get(rootId) || []
    const prefixParents = [...new Set(prefixCandidates.map(entry => entry.parentId))]
    const prefix = prefixCandidates.length === 1 && prefixParents.length === 1 ? prefixCandidates[0] : null
    if (root._tree.isTreeRoot) {
      if (firstParentIds.length || firstParentOptions.length || (ids.length === 1 && (explicitParentIds.length || explicitParentOptions.length))) return null
    } else {
      if (!prefix) return null
      const prefixParentId = prefix.parentId
      const prefixOptionId = idKey(prefix.option.id)
      const partialParentIds = ids.length === 1 ? [...firstParentIds, ...explicitParentIds] : firstParentIds
      const partialParentOptions = ids.length === 1 ? [...firstParentOptions, ...explicitParentOptions] : firstParentOptions
      if (new Set(partialParentIds).size > 1 || new Set(partialParentOptions).size > 1 ||
        (partialParentIds.length && partialParentIds[0] !== prefixParentId) ||
        (partialParentOptions.length && partialParentOptions[0] !== prefixOptionId)) return null
    }
    if (ids.length > 1 && explicitParentIds.length && explicitParentIds[0] !== ids[ids.length - 2]) return null
    if (ids.length > 1 && explicitParentOptions.length) {
      const entry = optionMap.get(explicitParentOptions[0])
      if (optionMap.has(explicitParentOptions[0]) && !entry) return null
      if (entry && entry.parentId !== ids[ids.length - 2]) return null
      const snapshotParentOptions = snapshotOptionIds(path.nodes[path.nodes.length - 2])
      if (snapshotParentOptions.invalid ||
        (snapshotParentOptions.ids.length && !snapshotParentOptions.ids.includes(explicitParentOptions[0]))) return null
      const terminalParentOptions = [path.nodes[path.nodes.length - 1].parent_option_id, path.nodes[path.nodes.length - 1].answer_parent_option_id]
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(idKey)
      if (terminalParentOptions.includes(null) || new Set(terminalParentOptions).size > 1 ||
        (terminalParentOptions.length && terminalParentOptions[0] !== explicitParentOptions[0])) return null
    }

    if (root._tree.isTreeRoot) return visibleIds.has(rootId) ? rootId : null
    // A partial historical snapshot proves only its own suffix. Its missing
    // prefix can come solely from unique configured ancestry.
    return configuredRootIdFor(root, new Set(ids.slice(1)), false)
  }

  function visibleRootIdFor (row) {
    const rowId = idKey(row.id)
    if (duplicateQuestionIds.has(rowId)) return null
    if (row._tree.isTreeRoot) return visibleIds.has(rowId) ? rowId : null

    // A child's own snapshot is historical evidence. It intentionally does not
    // borrow an intermediate row's current answer path or current branch.
    if (row._tree.hasAnswerPathSnapshot) {
      return historicalRootIdFor(row)
    }

    // Without a complete own snapshot, only independently configured edges
    // may establish ownership. Never borrow another row's current answer path.
    return configuredRootIdFor(row)
  }

  return visibleRows.map(row => ({
    ...row,
    _tree: {
      ...row._tree,
      isTreeQuestion: Boolean(isTree),
      isTreeRoot: Boolean(isTree) && row._tree.isTreeRoot,
      // 仅暴露可验证且实际可见的根题。历史断层题仍显示，但不会被提升为根题卡。
      rootQuestionId: visibleRootIdFor(row)
    }
  }))
}
